import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Pagination,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import VideoLibraryOutlined from "@mui/icons-material/VideoLibraryOutlined";
import { GlobalContext } from "../../../context/GlobalContext";
import { listVideoJobs } from "../api/listVideoJobs";
import { jobModelLabel, truncatePrompt } from "../utils/jobLabels";
import { mediaSrc } from "../../../utils/mediaSrc";

const PAGE_SIZE = 12;

function absoluteMediaUrl(stored) {
  const display = mediaSrc(stored) || String(stored || "").trim();
  if (!display) return "";
  if (/^https?:\/\//i.test(display)) return display;
  if (/^https?:\/\//i.test(String(stored || ""))) return String(stored).trim();
  if (typeof window === "undefined") return display;
  const path = display.startsWith("/") ? display : `/${display}`;
  return `${window.location.origin}${path}`;
}

function VideoPickTile({ job, selected, disabled, onToggle, lang }) {
  const hasVideo = job.status === "completed" && Boolean(job.video_url);
  const src = hasVideo ? mediaSrc(job.video_url) || job.video_url : "";

  return (
    <Box
      onClick={() => hasVideo && !disabled && onToggle(job.id)}
      sx={{
        position: "relative",
        borderRadius: 2,
        overflow: "hidden",
        border: "2px solid",
        borderColor: selected ? "primary.main" : "divider",
        opacity: hasVideo ? 1 : 0.45,
        cursor: hasVideo && !disabled ? "pointer" : "not-allowed",
        bgcolor: "#000",
      }}
    >
      <Checkbox
        checked={selected}
        disabled={!hasVideo || disabled}
        onChange={() => onToggle(job.id)}
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: "absolute",
          top: 4,
          left: 4,
          zIndex: 2,
          bgcolor: alpha("#fff", 0.9),
          borderRadius: 1,
          p: 0.25,
        }}
      />
      <Box sx={{ pt: "56.25%", position: "relative" }}>
        {src ? (
          <Box
            component="video"
            src={src}
            muted
            playsInline
            preload="metadata"
            sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              p: 1,
              textAlign: "center",
            }}
          >
            {lang?.videoStitchNotReady || "Not ready"}
          </Typography>
        )}
      </Box>
      <Box sx={{ p: 1, bgcolor: "background.paper" }}>
        <Typography variant="caption" fontWeight={800} display="block" noWrap>
          {jobModelLabel(job.ui_model)} · {job.duration_sec || "?"}s
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ fontSize: 10 }}>
          {truncatePrompt(job.prompt, 48) || `#${job.id}`}
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * Pick completed Video Studio clips as reference videos.
 * @param {{
 *   open: boolean;
 *   onClose: () => void;
 *   maxSelect?: number;
 *   onConfirm: (items: Array<{ id: string|number; previewUrl: string; name: string }>) => void;
 *   lang?: object;
 * }} props
 */
export default function PickVideoLibraryModal({ open, onClose, maxSelect = 1, onConfirm, lang }) {
  const { hitAxios, showSnack } = React.useContext(GlobalContext);
  const [loading, setLoading] = React.useState(false);
  const [jobs, setJobs] = React.useState([]);
  const [page, setPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(0);
  const [selectedIds, setSelectedIds] = React.useState(() => new Set());

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { jobs: rows, pagination } = await listVideoJobs(hitAxios, {
        page,
        limit: PAGE_SIZE,
        status: "completed",
      });
      setJobs(rows.filter((j) => j.status === "completed" && j.video_url));
      setTotalPages(pagination?.total_pages || 1);
    } catch (err) {
      showSnack?.(err?.message || lang?.videoStudioVideoLibraryLoadFailed || "Could not load videos.", false);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [hitAxios, page, showSnack, lang]);

  React.useEffect(() => {
    if (!open) return;
    setPage(1);
    setSelectedIds(new Set());
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    load();
  }, [open, load]);

  const slotsLeft = Math.max(0, maxSelect - selectedIds.size);

  const toggle = (jobId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else {
        if (next.size >= maxSelect) return prev;
        next.add(jobId);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    const picked = [];
    for (const job of jobs) {
      if (!selectedIds.has(job.id)) continue;
      const previewUrl = absoluteMediaUrl(job.video_url);
      if (!previewUrl) continue;
      picked.push({
        id: job.id,
        previewUrl,
        name: truncatePrompt(job.prompt, 40) || job.title || `Video #${job.id}`,
      });
    }
    if (picked.length) onConfirm(picked);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}>
        <VideoLibraryOutlined color="primary" />
        <Box sx={{ flex: 1 }}>{lang?.videoStudioVideoLibraryTitle || "Choose from Video Library"}</Box>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseOutlined />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {lang?.videoStudioVideoLibraryHint ||
              "Select a completed video to use as a reference clip."}
          </Typography>
          {maxSelect > 1 ? (
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              {(lang?.videoStudioLibrarySlotsLeft || "{n} slot(s) remaining").replace(
                "{n}",
                String(slotsLeft),
              )}
            </Typography>
          ) : null}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : jobs.length === 0 ? (
            <Alert severity="info">
              {lang?.videoStudioVideoLibraryEmpty ||
                "No completed videos yet. Generate clips in Video Studio first."}
            </Alert>
          ) : (
            <Grid container spacing={1.5}>
              {jobs.map((job) => (
                <Grid item xs={6} sm={4} md={3} key={job.id}>
                  <VideoPickTile
                    job={job}
                    selected={selectedIds.has(job.id)}
                    disabled={slotsLeft === 0 && !selectedIds.has(job.id)}
                    onToggle={toggle}
                    lang={lang}
                  />
                </Grid>
              ))}
            </Grid>
          )}

          {totalPages > 1 ? (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, p) => setPage(p)}
                color="primary"
                size="small"
              />
            </Box>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>{lang?.cancel || "Cancel"}</Button>
        <Button
          variant="contained"
          disabled={selectedIds.size === 0}
          onClick={handleConfirm}
          sx={{ fontWeight: 800 }}
        >
          {(lang?.videoStudioUseSelected || "Use selected") + ` (${selectedIds.size})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
